import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUserData } from '../../hooks/useUserData';
import { CashpurrFAB } from '../ui/cashpurr-fab';
import { LayoutDashboard, Target, Zap, DollarSign, Users, BookOpen, Settings, Bell, User, Menu, X, Wrench, MessageSquare, Book } from 'lucide-react';
import { TrialNotification } from '../ui/trial-notification';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCashpurr, setShowCashpurr] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { profile, tasks } = useUserData();

  // Calculate unread notifications count
  const unreadNotifications = tasks.filter(task => !task.completed).length;

  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: LayoutDashboard },
    { name: 'Idea Assessment', href: '/app/idea-validation', icon: Target },
    { name: 'Boss Mode', href: '/app/boss-mode', icon: Zap },
    { name: 'ROI Tracker', href: '/app/roi-tracker', icon: DollarSign },
    { name: 'Founder Journal', href: '/app/founder-journal', icon: BookOpen },
    { name: 'Social Tracker', href: '/app/social-tracker', icon: MessageSquare },
    { name: 'Prompt Library', href: '/app/prompts', icon: BookOpen },
    { name: 'Playbooks', href: '/app/playbooks', icon: Book },
    { name: 'Community Pods', href: '/app/community-pods', icon: Users },
    { name: 'Settings', href: '/app/settings', icon: Settings },
  ];

  const isActive = (href: string) => location.pathname === href || (href === '/app/dashboard' && location.pathname === '/app');

  return (
    <div className="flex h-screen bg-startup-slate">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-neumorphic transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative lg:flex lg:flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-startup-gray/20 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img 
              src="/StrtupStreak-Icon.png" 
              alt="StartupStreak logo" 
              className="h-8 w-8 rounded-lg object-contain"
            />
            <span className="text-xl font-bold text-startup-navy">StartupStreak</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-startup-gray hover:text-startup-navy"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 mt-6 px-3 overflow-y-auto">
          <div className="space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-startup-teal text-white shadow-lg'
                    : 'text-startup-gray hover:text-startup-navy hover:bg-startup-slate'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`mr-3 h-5 w-5 ${
                  isActive(item.href) ? 'text-white' : 'text-startup-gray group-hover:text-startup-navy'
                }`} />
                {item.name}
              </Link>
            ))}
          </div>
        </nav>

        {/* Notifications Panel */}
        {showNotifications && (
          <div className="p-3 flex-shrink-0 animate-slide-in">
            <div className="bg-startup-slate border-2 border-startup-teal rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-startup-navy">Notifications</h3>
                <button 
                  onClick={() => setShowNotifications(false)}
                  className="text-startup-gray hover:text-startup-navy"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-startup-gray mb-3">{unreadNotifications} pending tasks</p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {tasks.slice(0, 5).map((task) => (
                  <div key={task.id} className="p-2 bg-white rounded-lg hover:bg-startup-slate transition-colors">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${task.completed ? 'bg-green-500' : 'bg-startup-red'}`}></div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-startup-navy truncate">{task.title}</p>
                        <p className="text-xs text-startup-gray">{task.category}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Link 
                to="/app/boss-mode" 
                className="block text-center mt-3 text-xs text-startup-teal hover:text-startup-navy"
                onClick={() => setShowNotifications(false)}
              >
                View all tasks →
              </Link>
            </div>
          </div>
        )}

        {/* Profile Panel */}
        {showProfile && (
          <div className="p-3 flex-shrink-0 animate-slide-in">
            <div className="bg-startup-slate border-2 border-startup-teal rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-startup-navy">Profile</h3>
                <button 
                  onClick={() => setShowProfile(false)}
                  className="text-startup-gray hover:text-startup-navy"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-startup-teal rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {(profile?.full_name || user?.email || 'U')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-startup-navy text-sm">
                    {profile?.full_name || user?.email?.split('@')[0] || 'User'}
                  </p>
                  <p className="text-xs text-startup-gray truncate">{user?.email}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Link 
                  to="/app/settings" 
                  className="block px-3 py-2 text-xs text-startup-gray hover:text-startup-navy hover:bg-white rounded-lg transition-colors"
                  onClick={() => setShowProfile(false)}
                >
                  Settings
                </Link>
                <button 
                  onClick={() => { signOut(); setShowProfile(false); }}
                  className="w-full text-left px-3 py-2 text-xs text-startup-red hover:bg-red-50 rounded-lg transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cashpurr Sidebar Widget */}
        <div className="p-3 flex-shrink-0">
          <div className="bg-gradient-startup text-white rounded-xl p-4">
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 bg-startup-gold rounded-full flex items-center justify-center mr-3">
                <span className="text-lg">🐱</span>
              </div>
              <span className="font-semibold text-sm">Cashpurr</span>
            </div>
            <p className="text-white/90 text-xs leading-relaxed">
              "Keep clawing your way to success! Every task completed is a step closer to your first sale! 💰"
            </p>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navigation */}
        <header className="bg-white border-b border-startup-gray/20 h-16 flex items-center justify-between px-6">
          <div className="flex items-center">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)} 
              className="lg:hidden text-startup-gray hover:text-startup-navy mr-4"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-startup-navy">
              {navigation.find(item => isActive(item.href))?.name || 'Dashboard'}
            </h1>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-startup-gray hover:text-startup-navy rounded-lg hover:bg-startup-slate transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadNotifications > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-startup-red rounded-full"></span>
                )}
              </button>
            </div>

            {/* Profile */}
            <div className="relative">
              <button 
                onClick={() => setShowProfile(!showProfile)}
                className="flex items-center space-x-2 p-2 text-startup-gray hover:text-startup-navy rounded-lg hover:bg-startup-slate transition-colors"
              >
                <User className="w-5 h-5" />
                <span className="hidden md:block text-sm font-medium">
                  {profile?.full_name || user?.email?.split('@')[0] || 'Profile'}
                </span>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 bg-startup-slate">
          {children}
          <TrialNotification />
        </main>
        
        {/* Cashpurr FAB */}
        <CashpurrFAB />
      </div>
    </div>
  );
};

export default MainLayout;
